// useProcessingData.js
import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../helpers/axios";
import { useSelector, useDispatch } from "react-redux";
import { userActions } from "../actions/user.actions";
import { appContextConstants } from '../constants/appContext.constants';
const FILTER_KEY = "processingTableFilters";

const getSavedFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_KEY) || "null") || {};
  } catch {
    return {};
  }
};

const useProcessingData = ({
  status,
  user,
  defaultFundSource = "",
  dependencies = {},
}) => {
  const {
    processstate,
    datestate,
    usersearchstate,
  } = dependencies;
// const productType = sessionStorage.getItem("sanctionProductType");
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(
    sessionStorage.getItem("page")
      ? parseInt(sessionStorage.getItem("page"), 10)
      : 1
  );
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFetchRows, setTotalFetchRows] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const saved = getSavedFilters();

  const [filterDateFrom, setFilterDateFrom] = useState(saved.filterDateFrom || null);
  const [filterDateTo, setFilterDateTo] = useState(saved.filterDateTo || null);
  const [instituteId, setInstituteId] = useState(saved.instituteId || null);
  const [instituteName, setInstituteName] = useState(saved.instituteName || "");
  const [selectedCRA, setSelectedCRA] = useState(saved.selectedCRA || "");
  const [activePage, setActivePage] = useState(15);

  const productTypeSelected = useSelector((state) => state.ProductTypeFilter.selectedProductType);
  const isPersonalLoanSelected = productTypeSelected === "2";
  const [fundSourceSelected, setFundSourceSelected] = useState(
    saved.fundSourceSelected !== undefined ? saved.fundSourceSelected : defaultFundSource
  );
  const [fundSourceArray, setFundSourceArray] = useState([]);
  const dispatch = useDispatch();
  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({
        filterDateFrom,
        filterDateTo,
        instituteId,
        instituteName,
        selectedCRA,
        fundSourceSelected,
        productTypeSelected
      })
    );
  }, [filterDateFrom, filterDateTo, instituteId, instituteName, selectedCRA, fundSourceSelected]);

  const buildQueryParams = useCallback(
    (overrides = {}) => {
      const params = {};

      if (filterDateFrom && filterDateTo) {
        params.fromDate = filterDateFrom;
        params.toDate = filterDateTo + " 23:59:59";
      }

      if (instituteId) {
        params.instituteId = instituteId;
      }

      if (status) {
        params.status = status;
      }

      if (selectedCRA) {
        params.cra = selectedCRA;
      }
      if (productTypeSelected && productTypeSelected !== "all") {
        params.productType = Number(productTypeSelected);
      }
      if (fundSourceSelected && productTypeSelected !== "2") {
        params.fundSource = fundSourceSelected;
      }

      params.perPage = rowsPerPage;
      params.pageNo = currentPage;

      return { ...params, ...overrides };
    },
    [
      filterDateFrom,
      filterDateTo,
      instituteId,
      status,
      selectedCRA,
      productTypeSelected,
      fundSourceSelected,
      rowsPerPage,
      currentPage,
    ]
  );

 const fetchData = useCallback(
    async (queryOverrides = {}) => {
      setIsLoading(true);

      try {
        const queryParams = buildQueryParams(queryOverrides);

        const response = await axiosInstance.get(
          "/admin/application/in-progress",
          {
            params: queryParams,
            headers: {
            },
          }
        );

        const data = response?.data?.data;
        setTableData(data?.applicantDetails || []);
        setCurrentPage(data?.pagination?.currentPage || 1);
        setTotalFetchRows(data?.pagination?.totalRows || 0);
        setRowsPerPage(data?.pagination?.perPage || rowsPerPage);
        setFundSourceArray(data?.count || []);
        return data;
      } catch (error) {
        console.error("Error fetching in-progress applications:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [buildQueryParams, user, rowsPerPage, dispatch, productTypeSelected]
  );
 
  const handlePageChange = useCallback(
    (pageNumber) => {
      setCurrentPage(pageNumber);
      fetchData({ pageNo: pageNumber });
    },
    [fetchData]
  );

  const handleRowsPerPageChange = useCallback(
    (number) => {
      setActivePage(number);
      setRowsPerPage(number);
      fetchData({ perPage: number, pageNo: 1 });
    },
    [fetchData]
  );

  const handleSearch = useCallback(() => {
    // Uses current filterDateFrom, filterDateTo, instituteId, CRA, fundSourceSelected
    fetchData({ pageNo: 1 });
  }, [fetchData]);

  const handleDateFilter = useCallback((startDate, endDate) => {
    setFilterDateFrom(startDate);
    setFilterDateTo(endDate);
  }, []);

  const clearFilters = useCallback(() => {
    sessionStorage.removeItem(FILTER_KEY);
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setInstituteId(null);
    setInstituteName("");
    setSelectedCRA("");
    setFundSourceSelected(defaultFundSource);
    setCurrentPage(1);
    setActivePage(15);

    fetchData({
      fromDate: undefined,
      toDate: undefined,
      instituteId: undefined,
      cra: undefined,
      fundSource: defaultFundSource || undefined,
      pageNo: 1,
    });
  }, [defaultFundSource, fetchData]);

  const handleCRAChange = useCallback(
    (craValue) => {
      setSelectedCRA(craValue);
      fetchData({ cra: craValue, pageNo: 1 });
    },
    [fetchData]
  );

  const handleFundSourceFilter = useCallback(
    (fundSource, fromDate, toDate) => {
      setFundSourceSelected(fundSource);
      setCurrentPage(1);
      setActivePage(15);

      const overrides = { pageNo: 1 };
      if (fundSource) overrides.fundSource = fundSource;
      if (fromDate && toDate) {
        overrides.fromDate = fromDate;
        overrides.toDate = toDate + " 23:59:59";
      }

      fetchData(overrides);
    },
    [fetchData]
  );



  useEffect(() => {
    const shouldFetch = !usersearchstate;

    if (shouldFetch) {
      dispatch({ type: appContextConstants.SET_APP_CONTEXT, payload: { status } });
      fetchData();
    } else {
      setIsLoading(false);
      setTableData(usersearchstate?.applicantDetails || []);
      setCurrentPage(usersearchstate?.pagination?.currentPage || 1);
      setTotalFetchRows(usersearchstate?.pagination?.totalRows || 0);
      setRowsPerPage(usersearchstate?.pagination?.perPage || 10);
      setFundSourceArray(usersearchstate?.count || []);
    }
    // same pattern as your useApplicationData: no fetchData in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, processstate, datestate, usersearchstate, productTypeSelected]);

  return {
    tableData,
    currentPage,
    rowsPerPage,
    totalFetchRows,
    isLoading,
    filterDateFrom,
    filterDateTo,
    instituteId,
    instituteName,
    selectedCRA,
    activePage,
    fundSourceSelected,
    fundSourceArray,

    setFilterDateFrom,
    setFilterDateTo,
    setInstituteId,
    setInstituteName,
    setSelectedCRA,
    setCurrentPage,

    fetchData,
    handlePageChange,
    handleRowsPerPageChange,
    handleSearch,
    handleDateFilter,
    clearFilters,
    handleCRAChange,
    productTypeSelected,
    handleFundSourceFilter,
  };
};

export default useProcessingData;
